import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { formatCoinAmount } from '@nova-mine/shared';
import { NovaCoin3D } from './common/NovaCoin3D.js';
import { sound } from '../utils/sound.js';

interface NavbarProps {
  username?: string | null;
  firstName?: string;
  balance: number;
  isDemoMode?: boolean;
  onOpenProfile: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  username,
  firstName,
  balance,
  isDemoMode = false,
  onOpenProfile,
}) => {
  return (
    <header className="sticky top-0 z-30 w-full px-4 py-3 bg-midnight-950/70 backdrop-blur-2xl border-b border-white/[0.08] flex items-center justify-between select-none">
      {/* Brand & User Avatar */}
      <motion.div
        whileTap={{ scale: 0.96 }}
        onClick={() => {
          sound.playTap();
          onOpenProfile();
        }}
        className="flex items-center space-x-2.5 cursor-pointer group"
      >
        <div className="relative">
          <NovaCoin3D size="sm" animate={true} />
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border border-midnight-950 shadow-[0_0_6px_#34d399]" />
        </div>

        <div>
          <div className="flex items-center space-x-1.5">
            <span className="font-black text-sm tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-cyan-300 group-hover:from-cyan-300 group-hover:to-purple-400 transition-all">
              NOVA MINE
            </span>
            <ShieldCheck className="w-3.5 h-3.5 text-nova-cyan" />
            {isDemoMode && (
              <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-md bg-amber-500/20 text-nova-gold border border-amber-500/30">
                SANDBOX
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-400 font-medium">
            {firstName || username ? `@${username || firstName}` : 'Cloud Pioneer'}
          </p>
        </div>
      </motion.div>

      {/* Vault Balance Display */}
      <motion.div
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          sound.playTap();
          onOpenProfile();
        }}
        className="flex items-center space-x-2 bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/25 px-3 py-1.5 rounded-full shadow-lg shadow-amber-500/10 cursor-pointer hover:border-amber-500/40 transition-all"
      >
        <NovaCoin3D size="xs" />
        <span className="text-xs font-mono font-black text-gold-gradient tracking-tight">
          {formatCoinAmount(balance, 2)}
        </span>
        <span className="text-[10px] text-amber-300 font-extrabold uppercase">NOVA</span>
      </motion.div>
    </header>
  );
};
